import { Bell, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAlerts, useMarkAlertRead, useMarkAllAlertsRead } from '@/hooks/useAlerts';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const SEVERITY_COLOR = {
  info: 'text-blue-500',
  warning: 'text-amber-500',
  error: 'text-red-500',
  critical: 'text-red-500',
};

export function AlertsDropdown(): JSX.Element {
  const { data } = useAlerts({ unreadOnly: false });
  const markRead = useMarkAlertRead();
  const markAllRead = useMarkAllAlertsRead();

  const unreadCount = data?.unreadCount ?? 0;
  const alerts = data?.alerts ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground flex items-center justify-center px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="px-0 py-0">
            Alertas {unreadCount > 0 ? `(${unreadCount} não lidos)` : ''}
          </DropdownMenuLabel>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Marcar todos lidos
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        {alerts.length === 0 ? (
          <div className="px-4 py-8 text-sm text-muted-foreground text-center">
            Nenhum alerta. 🎉
          </div>
        ) : (
          <div className="max-h-[360px] overflow-auto">
            {alerts.slice(0, 20).map((a) => (
              <DropdownMenuItem
                key={a.id}
                className={cn('flex flex-col items-start gap-0.5 py-2 cursor-pointer', !a.lido_em && 'bg-accent/40')}
                onSelect={(e) => {
                  e.preventDefault();
                  if (!a.lido_em) markRead.mutate(a.id);
                }}
              >
                <div className="flex items-center gap-2 w-full">
                  <span className={cn('h-1.5 w-1.5 rounded-full', SEVERITY_COLOR[a.severidade], 'bg-current')} />
                  <span className="font-medium text-sm flex-1 truncate">{a.titulo}</span>
                  {!a.lido_em && <span className="text-xs text-muted-foreground">novo</span>}
                </div>
                {a.descricao && (
                  <p className="text-xs text-muted-foreground line-clamp-2 pl-3.5">{a.descricao}</p>
                )}
                <div className="flex items-center gap-2 w-full pl-3.5 text-xs text-muted-foreground">
                  <span>{new Date(a.criado_em).toLocaleString('pt-BR')}</span>
                  {a.acao_url && (
                    <Link to={a.acao_url} className="ml-auto text-primary hover:underline">
                      Abrir →
                    </Link>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
